import { useTranslations } from 'next-intl';
import { type FC, useState } from 'react';
import { More } from '@/app/components/icons/More';
import { VoteButton } from '@/app/components/shared/VoteButton';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { useDevice } from '@/app/hooks';
import type { CreationType, EllipsisActionTypeEnum, ReportParams, VoteType } from '@/app/types';

interface Action {
  action?: EllipsisActionTypeEnum;
  key?: string;
  label?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

interface EllipsisDropdownMenuProps {
  creationType?: CreationType;
  actions: Action[];
  className?: string;
  side?: 'top' | 'bottom';
  onSelectedAction: (action: EllipsisActionTypeEnum) => void;
  onDropdownOpenChange?: (open: boolean) => void;
  onVote?: (vote?: VoteType) => void;
  currentVote?: VoteType;
  creationId?: string;
  // 用于上报事件
  reportParams?: ReportParams;
}

type ActionKey = 'report' | 'delete' | 'inspirationreport' | 't2afeedback';

export const EllipsisDropdownMenu: FC<EllipsisDropdownMenuProps> = ({
  creationType,
  onSelectedAction,
  actions,
  className,
  side,
  onDropdownOpenChange,
  onVote,
  currentVote,
  creationId,
  reportParams,
}) => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  // TODO(xiudan): 统一的问题，在 iphone 上不支持点击打开弹窗
  const [open, setOpen] = useState<boolean | undefined>(isMobile ? false : undefined);
  const handleClickOpen = () => {
    if (isMobile) setOpen(true);
  };
  const handleClickClose = () => {
    if (isMobile) setOpen(false);
  };
  return (
    <DropdownMenu open={open} onOpenChange={onDropdownOpenChange}>
      <DropdownMenuTrigger asChild onClick={handleClickOpen}>
        <Button variant="text" size="original" className={className}>
          <More />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        side={side}
        sideOffset={4}
        onPointerDownOutside={handleClickClose}
        collisionPadding={{
          left: 12,
          right: 16,
        }}
      >
        {creationId && creationType !== 'mini_video' && (
          <VoteButton id={creationId} vote={currentVote ?? 'unspecified'} onVote={onVote} reportParams={reportParams} />
        )}

        {actions.map(({ action, key, label, onClick, icon }) => (
          <DropdownMenuItem
            key={`ellipsis_action_${action ?? key}`}
            className="cursor-pointer text-sm md:text-xs"
            onClick={() => {
              if (action) {
                onSelectedAction(action);
                return;
              }
              onClick?.();
            }}
          >
            {icon && <span className="mr-2 shrink-0 text-lg md:text-base">{icon}</span>}
            {action ? t(`creation.action.${action.toLowerCase() as ActionKey}`) : label}
          </DropdownMenuItem>
        ))}

        <DropdownMenuArrow />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
